import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
} from 'src/util/constants';
import getNodesFromEdges from 'src/util/graphql';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { valuePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';
import { buildTradePointTradeRuleTableLabel } from 'src/util/i18n/tradePoint';

import TradeRuleListTableItemClause from './TradeRuleListTableItemClause';
import UUID from '../../UUID';

const TradeRuleListTableItemFragment = graphql`
  fragment TradeRuleListTableItem_rule on TradeRule {
    id
    buyer {
      user { id email givenName familyName }
      community { id title }
      residualId
      tradePoint {
        id
        type
        meter {
          id
          identifier
          title
          property {
            id
            title
          }
        }
      }
    }
    seller {
      user { id email givenName familyName }
      community { id title }
      residualId
      tradePoint {
        id
        type
        meter {
          id
          identifier
          title
          property {
            id
            title
          }
        }
      }
    }
    clauses {
      edges {
        node {
          id
          price
          ignoreDaylightSavings
          ignorePublicHolidays
          timezone
          ...TradeRuleListTableItemClause_clause
        }
      }
    }
    tradeType
    state
    start
    finish
    proposedAt
    proposedBy {id email givenName familyName }
    acceptedAt
    acceptedBy {id email givenName familyName }
    rejectedAt
    rejectedBy {id email givenName familyName }
    cancelledAt
    cancelledBy {id email givenName familyName }
    closedAt
    closedBy {id email givenName familyName }
  }
`;

/**
 * Determine the trade direction relative to a given trade point.
 * @param {string} tradePointId - The trade point ID to compare against
 * @param {object} rule - The trade rule object containing buyer and seller data
 * @returns {string} Trade direction constant (BUY, SELL, or UNSPECIFIED)
 */
export const getDirection = (tradePointId, rule) => {
  const { buyer, seller } = rule;

  let buyerTradePointId = null;
  let sellerTradePointId = null;

  if (buyer.tradePoint && buyer.tradePoint.id) {
    buyerTradePointId = buyer.tradePoint.id;
  }
  if (seller.tradePoint && seller.tradePoint.id) {
    sellerTradePointId = seller.tradePoint.id;
  }

  if (buyerTradePointId && buyerTradePointId === tradePointId) {
    return TRADE_DIRECTION_BUY;
  }
  if (sellerTradePointId && sellerTradePointId === tradePointId) {
    return TRADE_DIRECTION_SELL;
  }
  return TRADE_DIRECTION_UNSPECIFIED;
};

/**
 * Get the counter-party trader (user, community, or residual) for a trade rule.
 * @param {string} tradePointId - The trade point ID to determine counter-party
 * @param {object} rule - The trade rule object containing buyer and seller data
 * @returns {object|null} Counter-party trader object or null if not found
 */
export const getCounterpartyTrader = (tradePointId, rule) => {
  const direction = getDirection(tradePointId, rule);

  let trader;
  if (direction === TRADE_DIRECTION_BUY) {
    trader = rule.seller;
  } else if (direction === TRADE_DIRECTION_SELL) {
    trader = rule.buyer;
  } else {
    return null;
  }

  if (trader.user) {
    return trader.user;
  }
  if (trader.community) {
    return trader.community;
  }
  if (trader.residual) {
    return trader.residual;
  }

  return null;
};

/* Get the counter-party trade point label for a trade rule.
 * @param {import('react-intl').IntlShape} intl - React Intl object for formatting
 * @param {string} tradePointId - The trade point ID to determine counter-party
 * @param {object} rule - The trade rule object containing buyer and seller data
 * @returns {React.ReactNode|null} Formatted trade point label component or null if not found
 */
export const getCounterpartyTradePoint = (intl, tradePointId, rule) => {
  const direction = getDirection(tradePointId, rule);

  if (direction === TRADE_DIRECTION_UNSPECIFIED) {
    return null;
  }

  const tradePoint = direction === TRADE_DIRECTION_BUY
    ? rule.seller?.tradePoint
    : rule.buyer?.tradePoint;

  const tradePointLabel = tradePoint ? buildTradePointTradeRuleTableLabel(intl, tradePoint) : null;
  return tradePointLabel;
};

/**
 * Renders a single row in the trade rule list table.
 * Displays trade rule information including type, state, direction, counter-party details,
 * pricing clauses, and validity period.
 * @param {object} props - Component props
 * @param {string} props.propertyId - The property ID for navigation links
 * @param {object} props.rule - Relay fragment ref for TradeRule
 * @param {string} props.tradePointId - The current trade point ID for direction calculation
 * @returns {React.ReactElement} Table row element with trade rule data
 */
function TradeRuleListTableItem({ propertyId, rule: ruleData, tradePointId }) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  if (!ruleData) {
    return <Loading />;
  }

  const rule = useFragment(TradeRuleListTableItemFragment, ruleData);

  const {
    id, tradeType, state, start, finish,
  } = rule;
  const active = { start, finish };
  const clauses = getNodesFromEdges(rule.clauses).sort((a, b) => (b.price - a.price));

  const counterPartyTradePoint = getCounterpartyTradePoint(intl, tradePointId, rule);
  const counterPartyTrader = getCounterpartyTrader(tradePointId, rule);
  const direction = getDirection(tradePointId, rule);

  return (
    <tr key={`trade-rule-${id}`}>
      <th>
        <Link to={`/properties/${propertyId}/trade-rules/${id}`}>
          <UUID uuid={id} />
        </Link>
      </th>
      <td>
        <BadgeTradeType type={tradeType} />
      </td>
      <td>
        <BadgeTradeRuleState state={state} />
      </td>
      <td>
        <BadgeTradeDirection direction={direction} />
      </td>
      <td>
        {counterPartyTrader && (
          <>
            <strong>{displayUserName(intl, counterPartyTrader)}</strong>
            <br />
            <UUID uuid={counterPartyTrader.id} />
          </>
        )}
      </td>
      <td>
        {counterPartyTradePoint}
      </td>
      <td>
        {clauses.length === 0
          && (
          <p style={{ display: 'flex' }}>
            {valuePerUnitWithStrong(intl, intlCurrency, undefined)}
          </p>
          )}
        {clauses.length > 0
        && (
        <ul className="ps-2">
          {clauses?.map((clause) => (
            <TradeRuleListTableItemClause
              clause={clause}
              key={clause.id}
              showConditions={false}
            />
          ))}
        </ul>
        )}
      </td>
      <td data-testid="validity">
        {buildTimeRangeSummary(intl, active)}
      </td>
    </tr>
  );
}

TradeRuleListTableItem.propTypes = {
  propertyId: PropTypes.string.isRequired,
  rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  tradePointId: PropTypes.string.isRequired,
};

export default TradeRuleListTableItem;
